import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';

import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER,
  hasRoleAdmin,
} from 'common/utility/userRoles';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_BILLING } from 'client/constants/screenIds';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import PartnerActions from 'client/actions/Partner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles, { getUserLandingPage } from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const getFormFields = ({ isAdmin }) => {
  return [
    {
      id: 'utilityRevenueShare',
      title: '% of Revenue to Partner',
      type: 'percentage',
      properties: {
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
  ];
};

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_BILLING)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
}))
class UtilityPartnerSettingsBillingPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Billing',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
    };
  }

  _onSubmitForm = (formValues) => {
    const { partner, dispatch, history } = this.props;

    // TODO(@Sunroom): Implement SR_API_ACTION_UTILITY_PARTNER_UPDATE_UTILITY_SETTINGS.
    dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_UTILITY_PARTNER_UPDATE_UTILITY_SETTINGS',
      payload: {
        partnerId: partner.id,
        updates: formValues,
      },
    }).then(() => {
      dispatch(PartnerActions.loadPartnerBySlug, {
        slug: partner.slug,
        forceRefresh: true,
      });
      this.setState({
        shouldShowSavedModal: true,
      });
      setTimeout(() => {
        history.push(`/utility-profit/${partner.slug}/settings`);
      }, 800);
    });
  };

  render() {
    const { partner, currentUser, isTransitioning } = this.props;

    const { shouldShowSavedModal } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const isAdmin = hasRoleAdmin(currentUser.roles);
    const formFields = getFormFields({ isAdmin });

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader="Billing Settings"
      >
        <SrBox marginBottom="100px">
          <Formik
            enableReinitialize
            onSubmit={this._onSubmitForm}
            initialValues={createInitialValues({
              formFields: getFormFields({ isAdmin }),
              defaultValues: partner,
            })}
            validate={createFormValidationFn(formFields)}
            render={(formikBag) => {
              return (
                <SrBox marginBottom="100px">
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_YELLOW}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                      header="Revenue Share"
                    >
                      <SrBox width="400px">
                        {formFields.map((formField) => (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        ))}
                      </SrBox>
                    </SrCard>
                    {isAdmin && (
                      <SrStaticFooter>
                        <div />
                        <SrFooterSaveButton isSubmitting={false} />
                      </SrStaticFooter>
                    )}
                  </Form>
                </SrBox>
              );
            }}
          />
        </SrBox>
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsBillingPage.displayName = 'UtilityPartnerSettingsBillingPage';

export default UtilityPartnerSettingsBillingPage;
